import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEMICalculator } from "@/hooks/useEMICalculator";

export const EMICalculator = () => {
  const {
    loanAmount,
    setLoanAmount,
    years,
    setYears,
    emiData,
    loading,
    numberToWords
  } = useEMICalculator();

  // Local debounced states
  const [debouncedLoan, setDebouncedLoan] = useState(loanAmount);
  const [debouncedYears, setDebouncedYears] = useState(years);

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoanAmount(debouncedLoan);
      setYears(debouncedYears);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [debouncedLoan, debouncedYears, setLoanAmount, setYears]);

  return (
    <div className="emi-calculator" id="emi-calculator" data-testid="emi-calculator">
      <h3 className="emi-title">EMI Calculator</h3>

      <div className="emi-form">

        {/* Loan Amount */}
        <div className="form-group">
          <Label>Loan Amount</Label>
          <Input
            type="number"
            min={0}
            placeholder="Enter amount"
            value={debouncedLoan || ""}
            onChange={(e) => {
              const val = e.target.value;
              setDebouncedLoan(val === "" ? "" : Math.max(Number(val), 0));
            }}
            data-testid="emi-amount-input"
          />
          {debouncedLoan > 0 && (
            <p className="amount-words">
              {numberToWords(Number(debouncedLoan))}
            </p>
          )}
        </div>

        {/* Tenure */}
        <div className="form-group">
          <Label>Tenure (Years)</Label>
          <Input
            type="number"
            min={0}
            placeholder="Enter years"
            value={debouncedYears || ""}
            onChange={(e) => {
              const val = e.target.value;
              setDebouncedYears(val === "" ? "" : Math.max(Number(val), 0));
            }}
            data-testid="emi-years-input"
          />
          {debouncedYears > 0 && (
            <p className="months-display">{Number(debouncedYears) * 12} months</p>
          )}
        </div>

        {/* Loader */}
        {loading && <div className="emi-loading">Calculating...</div>}

        {/* Results */}
        {emiData && !loading && (
          <div className="emi-result" data-testid="emi-result">

            <div className="result-item">
              <span>Monthly EMI:</span>
              <strong>
                ₹{emiData?.monthlyEMI?.toLocaleString("en-IN")}
              </strong>
            </div>

            <div className="result-item">
              <span>Total Interest:</span>
              <strong>
                ₹{emiData?.totalInterest?.toLocaleString("en-IN")}
              </strong>
            </div>

            <div className="result-item">
              <span>Total Amount:</span>
              <strong>
                ₹{emiData?.totalAmount?.toLocaleString("en-IN")}
              </strong>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};
